import type { OrderItem } from '../../graphql/query/getOrder';
import type {
  Attribute,
  ProductVariant,
} from '../../graphql/query/getResolutionOptions';
import { type WorkflowData } from '../../workflow';

export const getMatchingVariant = ({
  variants,
  attributes,
}: {
  variants: ProductVariant[];
  attributes: Attribute[];
}) => {
  if (attributes.length === 0) return undefined;
  return variants.find((variant) =>
    attributes.every((attr) =>
      variant.attributes.some(
        (a) => a.name === attr.name && a.value === attr.value,
      ),
    ),
  );
};

export const getAvailableAttrValues = ({
  variants,
  name,
  selectedAttrs,
}: {
  variants: ProductVariant[];
  name: string;
  selectedAttrs: Record<string, string>;
}) => {
  const otherSelectedAttrs = Object.entries(selectedAttrs)
    .filter(([attrName, value]) => value && attrName !== name)
    .map(([attrName, value]) => ({ name: attrName, value }));

  const matchingVariants =
    otherSelectedAttrs.length > 0
      ? variants.filter((variant) =>
          otherSelectedAttrs.every((attr) =>
            variant.attributes.some(
              (a) => a.name === attr.name && a.value === attr.value,
            ),
          ),
        )
      : variants;

  return [
    ...new Set(
      matchingVariants
        .filter((variants) => variants.available)
        .map(
          (variant) => variant.attributes.find((a) => a.name === name)?.value,
        )
        .filter(Boolean),
    ),
  ];
};

export const updateWorkflowOnBack = ({
  item,
  data,
}: {
  item?: OrderItem;
  data: WorkflowData | null;
}) => {
  if (!data?.items || !item) {
    throw new Error('Cannot resolve without item data');
  }
  const workflowItemData = data.items?.[item.id];
  if (!workflowItemData) {
    throw new Error('Cannot resolve without item data');
  }

  return {
    ...data,
    items: {
      ...data.items,
      [item.id]: {
        ...workflowItemData,
        resolution:
          workflowItemData.resolution?.resolutionId &&
          workflowItemData.resolution?.exchangeItem
            ? { ...workflowItemData.resolution }
            : {},
      },
    },
  };
};
